// utils/LoanSimulator.ts

export interface SimulateLoanInput {
  principalAmount: number;
  interestRate: number;
  loanDuration: number;
  durationPeriod: 'MT' | 'TR' | 'SM' | 'AN' | string;
  amortizationCode: 'AL' | 'FR' | 'AM' | string;
  zeroQuota?: boolean;
  loanReleasedDate?: string; // formato ISO
}

export interface LoanPaymentProjection {
  shareNumber: number;
  date: string; // formato 'YYYY-MM-DD'
  capital: number;
  interest: number;
  quota: number;
  balance: number;
}

export interface LoanSimulationResult {
  administrativeExpenses: number;
  disbursedAmount: number;
  payments: LoanPaymentProjection[];
}

export function simulateLoanPayments({
  principalAmount,
  interestRate,
  loanDuration,
  durationPeriod,
  amortizationCode,
  zeroQuota = false,
  loanReleasedDate,
}: SimulateLoanInput): LoanSimulationResult {
  const cycleMap: Record<string, number> = {
    MT: 1,
    TR: 3,
    SM: 6,
    AN: 12,
  };

  const cycle = cycleMap[durationPeriod.toUpperCase()] || 1;
  const totalPayments = Math.floor(loanDuration);
  const principal = principalAmount;
  const rate = interestRate / 100;
  const releaseDate = loanReleasedDate ? new Date(loanReleasedDate) : new Date();

  const payments: LoanPaymentProjection[] = [];

  let cutoffDate = new Date(releaseDate);
  if (zeroQuota) {
    cutoffDate.setMonth(cutoffDate.getMonth() + 1);
    cutoffDate.setDate(1);
  }

  if (zeroQuota && releaseDate < cutoffDate) {
    const msInDay = 1000 * 60 * 60 * 24;
    const daysBetween = Math.round((cutoffDate.getTime() - releaseDate.getTime()) / msInDay);
    const dailyRate = rate / (cycle * 30);
    const interest = parseFloat((principal * dailyRate * daysBetween).toFixed(2));

    payments.push({
      shareNumber: 0,
      date: cutoffDate.toISOString().split('T')[0],
      capital: 0,
      interest,
      quota: interest,
      balance: principal,
    });
  }

  let firstPaymentDate = new Date(cutoffDate);
  firstPaymentDate.setMonth(firstPaymentDate.getMonth() + cycle);
  let remainingBalance = principal;

  if (amortizationCode === 'AL') {
    const fixedCapital = principal / totalPayments;
    for (let i = 1; i <= totalPayments; i++) {
      const interest = parseFloat((remainingBalance * rate).toFixed(2));
      remainingBalance -= fixedCapital;
      const date = new Date(firstPaymentDate);
      date.setMonth(date.getMonth() + (i - 1) * cycle);

      payments.push({
        shareNumber: i,
        date: date.toISOString().split('T')[0],
        capital: parseFloat(fixedCapital.toFixed(2)),
        interest,
        quota: parseFloat((fixedCapital + interest).toFixed(2)),
        balance: parseFloat(remainingBalance.toFixed(2)),
      });
    }
  } else if (amortizationCode === 'FR') {
    const monthlyPayment = parseFloat(
      (principal * (rate / (1 - Math.pow(1 + rate, -totalPayments)))).toFixed(2)
    );

    for (let i = 1; i <= totalPayments; i++) {
      const interest = parseFloat((remainingBalance * rate).toFixed(2));
      let capital = monthlyPayment - interest;
      if (i === totalPayments) capital = remainingBalance;

      const date = new Date(firstPaymentDate);
      date.setMonth(date.getMonth() + (i - 1) * cycle);

      payments.push({
        shareNumber: i,
        date: date.toISOString().split('T')[0],
        capital: parseFloat(capital.toFixed(2)),
        interest,
        quota: monthlyPayment,
        balance: parseFloat((remainingBalance - capital).toFixed(2)),
      });

      remainingBalance -= capital;
    }
  } else if (amortizationCode === 'AM') {
    const interestFlat = parseFloat((principal * rate).toFixed(2));

    for (let i = 1; i <= totalPayments; i++) {
      const date = new Date(firstPaymentDate);
      date.setMonth(date.getMonth() + (i - 1) * cycle);
      const quota = i === totalPayments ? parseFloat((principal + interestFlat).toFixed(2)) : interestFlat;

      payments.push({
        shareNumber: i,
        date: date.toISOString().split('T')[0],
        capital: i === totalPayments ? principal : 0,
        interest: interestFlat,
        quota,
        balance: i === totalPayments ? 0 : principal,
      });
    }
  } else {
    throw new Error('Tipo de amortización no reconocido');
  }

  // 🧮 Calcular gastos administrativos
  const administrativeExpenses = calculateAdministrativeExpense(principal);
  const disbursedAmount = parseFloat((principal - administrativeExpenses).toFixed(2));

  return {
    administrativeExpenses,
    disbursedAmount,
    payments,
  };
}

function calculateAdministrativeExpense(principal: number): number {
  const ranges: [number, number, number, number][] = [
    [0, 11000, 0.04, 0],
    [11000, 13000, 0.0375, 0],
    [13000, 15000, 0.035, 0],
    [15000, 16000, 0.0345, 0],
    [16000, 20000, 0.0325, 0],
    [20000, 21000, 0.0345, 0],
    [21000, 22000, 0.03, 0],
    [22000, 25000, 0.027, 0],
    [25000, 50000, 0.028, 400],
    [50000, 75000, 0.03, 475],
    [75000, 90000, 0.03, 500],
    [90000, 100000, 0.03, 700],
    [100000, 125000, 0.035, 1000],
    [125000, 500000, 0.04, 2500],
  ];

  for (const [min, max, percent, fixed] of ranges) {
    if (principal > min && principal <= max) {
      return Math.round(fixed + principal * percent);
    }
  }

  return 0;
}
